import type { StrategiesSSEEvent } from "../api/types";
import type { AiBlock, AiTextBlock, AiThoughtBlock, AiToolBlock } from "../types";

function getErrorMessage(event: StrategiesSSEEvent): string | null {
  if (typeof (event as { type?: unknown }).type === "string") {
    const typed = event as { type: string; error?: unknown };
    if (typed.type === "error" && typeof typed.error === "string") return typed.error;
  }

  const untyped = event as { error?: unknown };
  if (typeof untyped.error === "string") return untyped.error;

  return null;
}

function pickFlowStep(event: Extract<StrategiesSSEEvent, { type: "tool_result" }>):
  | AiToolBlock["flowStep"]
  | undefined {
  if (typeof event.flowStep === "number") return event.flowStep;
  const candidate = event.data?.flowStep;
  return typeof candidate === "number" ? candidate : undefined;
}

function pickTriggerBacktest(event: Extract<StrategiesSSEEvent, { type: "tool_result" }>):
  | AiToolBlock["triggerBacktest"]
  | undefined {
  if (typeof event.triggerBacktest === "boolean") return event.triggerBacktest;
  const candidate = event.data?.triggerBacktest;
  return typeof candidate === "boolean" ? candidate : undefined;
}

const VALID_BACKTEST_SCENARIOS = ["baseline", "sentiment", "ai_baseline", "ai_sentiment"] as const;
type ValidBacktestScenario = typeof VALID_BACKTEST_SCENARIOS[number];

function isValidScenario(v: unknown): v is ValidBacktestScenario {
  return typeof v === "string" && (VALID_BACKTEST_SCENARIOS as readonly string[]).includes(v);
}

function pickTriggerBacktestScenario(
  event: Extract<StrategiesSSEEvent, { type: "tool_result" }>,
): AiToolBlock["triggerBacktestScenario"] | undefined {
  if (isValidScenario(event.triggerBacktestScenario)) {
    return event.triggerBacktestScenario;
  }
  const candidate = event.data?.triggerBacktestScenario;
  return isValidScenario(candidate) ? candidate : undefined;
}

export function mapSSEEventToAiBlocks(event: StrategiesSSEEvent): AiBlock[] {
  const errorMessage = getErrorMessage(event);
  if (errorMessage) {
    const block: AiTextBlock = { type: "text", content: `Error: ${errorMessage}` };
    return [block];
  }

  if (!("type" in event)) return [];

  if (event.type === "thinking") {
    const block: AiThoughtBlock = {
      type: "thought",
      time: "0s",
      content: event.content,
    };
    return [block];
  }

  if (event.type === "message") {
    const block: AiTextBlock = { type: "text", content: event.content };
    return [block];
  }

  if (event.type === "tool_result") {
    const flowStep = pickFlowStep(event);
    const triggerBacktest = pickTriggerBacktest(event);
    const triggerBacktestScenario = pickTriggerBacktestScenario(event);

    const content = event.message?.trim()
      ? event.message
      : event.name?.trim()
        ? `Called ${event.name} tool`
        : "Tool result";

    const block: AiToolBlock = {
      type: "tool",
      content,
      toolName: event.name?.trim() || undefined,
      flowStep,
      triggerBacktest,
      triggerBacktestScenario,
    };
    return [block];
  }

  if (event.type === "meta") return [];
  if (event.type === "stage") return [];
  if (event.type === "tool_call_delta") return [];
  if (event.type === "done") return [];

  return [];
}
